import React, { useEffect } from 'react';
import styled from 'styled-components';
import { useHistory } from 'react-router-dom';
import {
  Grid,
  Table,
  Button,
  TableRow,
  TableCell,
  TableBody,
  TableHead,
  Typography,
  TableContainer,
  LinearProgress,
  TableSortLabel,
  TablePagination,
  CircularProgress
} from '@material-ui/core';
import { Add as AddIcon } from '@material-ui/icons';

import AppSnackbar from '../../common/AppSnackbar';
import TaskStatusLabel from '../../common/TaskStatusLabel';
import WrappedTableCell from '../../common/WrappedTableCell';
import { formatDate, formatCurrency } from '../../common/formatter';
import { PaginatedList } from '../../common/PaginatedList.interface';
import {
  Supplier,
  SupplierPurchaseOrder
} from '../supplier/Supplier.interface';
import { useCreateSpor } from '../supplier/Supplier.hooks';
import SporStatusFilter from './components/SporStatusFilter';

const StyledGrid = styled(Grid)`
  align-items: center;
  .actions {
    padding-left: 10px;
    padding-right: 10px;
  }
  .status-filter {
    min-width: 200px;
  }
`;

interface SporColumn {
  name: string;
  label: string;
}

const SporColumns: SporColumn[] = [
  { name: 'spor.id', label: 'ID' },
  { name: 'supplier.companyName', label: 'Supplier' },
  { name: 'task.status', label: 'Status' },
  { name: 'spor.taxes', label: 'Taxes' },
  { name: 'spor.fees', label: 'Fees' },
  { name: 'spor.shipping', label: 'Shipping' },
  { name: 'spor.totalCost', label: 'Total Cost' },
  { name: 'spor.orderDate', label: 'Order Date' },
  { name: 'spor.created', label: 'Created Date' }
];

export interface SporListViewProps {
  data: PaginatedList<SupplierPurchaseOrder> | undefined;
  supplier?: Supplier;
  isLoading?: boolean;
  showCreate?: boolean;
  order: string | undefined;
  orderDirection: string | undefined;
  status?: string[];
  setStatus?: (status?: string[]) => void;
  setPage: (page: number) => void;
  setLimit: (limit: number) => void;
  setOrder: (order: string) => void;
  setOrderDirection: (orderDirection: string) => void;
  handleAddItem?: (callback?: () => void) => void;
}

const SporListView: React.FC<SporListViewProps> = (
  props: SporListViewProps
) => {
  const history = useHistory();
  const {
    data,
    supplier,
    isLoading = false,
    showCreate = false,
    order,
    status,
    orderDirection,
    setPage,
    setLimit,
    setOrder,
    setStatus,
    setOrderDirection,
    handleAddItem
  } = props;
  const { spor, isCreating, createError, createSpor } = useCreateSpor();

  const handleSort = (column: string) => {
    setOrder(column);
    setOrderDirection(orderDirection === 'ASC' ? 'DESC' : 'ASC');
  };

  const getSortDirection = (column: string): 'asc' | 'desc' => {
    return order === column && orderDirection === 'DESC' ? 'desc' : 'asc';
  };

  useEffect(() => {
    handleAddItem?.(() => {
      if (!isCreating && supplier) {
        createSpor({ supplierId: supplier.id });
      }
    });
    return () => handleAddItem?.(undefined);
  }, [supplier, isCreating, createSpor, handleAddItem]);

  useEffect(() => {
    if (spor) {
      const paramsStr: string = supplier?.taskId
        ? `?supplierTaskId=${supplier.taskId}`
        : '';
      history.push(`/portal/purchase/form/${spor.taskId}${paramsStr}`);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [spor]);

  return (
    <>
      <TableContainer>
        <Table stickyHeader>
          <TableHead>
            <TableRow>
              {SporColumns.map((column: SporColumn) => (
                <TableCell key={column.name}>
                  <TableSortLabel
                    active={order === column.name}
                    direction={getSortDirection(column.name)}
                    onClick={() => handleSort(column.name)}
                  >
                    {column.label}
                  </TableSortLabel>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {isLoading && (
              <TableRow>
                <TableCell colSpan={SporColumns.length} padding='none'>
                  <LinearProgress />
                </TableCell>
              </TableRow>
            )}
            {!isLoading && data && data.data.length === 0 && (
              <TableRow>
                <TableCell
                  colSpan={SporColumns.length}
                  style={{ borderBottom: 'unset' }}
                >
                  <Typography variant='body1'>No records found.</Typography>
                </TableCell>
              </TableRow>
            )}
            {data?.data.map((spor: SupplierPurchaseOrder) => (
              <TableRow
                hover
                key={spor.id}
                onClick={(
                  event: React.MouseEvent<HTMLTableRowElement, MouseEvent>
                ) => {
                  event.stopPropagation();
                  history.push(`/portal/spors/task/${spor.taskId}`);
                }}
              >
                <TableCell>SPOR-{spor.id}</TableCell>
                <WrappedTableCell>
                  {spor.supplier?.companyName}
                </WrappedTableCell>
                <TableCell>
                  <TaskStatusLabel task={spor.task} />
                </TableCell>
                <TableCell>{formatCurrency(spor.taxes)}</TableCell>
                <TableCell>{formatCurrency(spor.fees)}</TableCell>
                <TableCell>{formatCurrency(spor.shipping)}</TableCell>
                <TableCell>{formatCurrency(spor.totalCost)}</TableCell>
                <TableCell>{formatDate(spor.orderDate)}</TableCell>
                <TableCell>{formatDate(spor.created)}</TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </TableContainer>
      <StyledGrid container>
        <Grid item className='actions'>
          <Grid container spacing={1}>
            {showCreate && (
              <Grid item>
                <Button
                  color='primary'
                  variant='outlined'
                  disabled={isCreating || !supplier}
                  endIcon={
                    isCreating ? <CircularProgress size={20} /> : <AddIcon />
                  }
                  onClick={() => {
                    supplier && createSpor({ supplierId: supplier.id });
                  }}
                >
                  Add Item
                </Button>
              </Grid>
            )}
            <Grid item>
              <SporStatusFilter value={status} onChange={setStatus} />
            </Grid>
          </Grid>
        </Grid>
        <Grid item>
          <TablePagination
            rowsPerPageOptions={[10, 25, 50, 100]}
            component='div'
            count={data?.total || 0}
            rowsPerPage={data?.limit || 100}
            page={data?.page ? data?.page - 1 : 0}
            onPageChange={(_, page: number) => setPage(page)}
            onRowsPerPageChange={(
              event: React.ChangeEvent<HTMLInputElement>
            ) => {
              setLimit(+event.target.value);
              setPage(0);
            }}
          />
        </Grid>
      </StyledGrid>

      <AppSnackbar severity='error' open={!!createError}>
        {createError?.message}
      </AppSnackbar>
    </>
  );
};

export default SporListView;
